<template>
  <Transition
    name="squish"
    @after-enter="adjustMenuPosition">
    <div
      v-show="visible"
      ref="menu"
      class="menu"
      :style="style">
      <slot></slot>
    </div>
  </Transition>
</template>

<script setup lang="ts">
  import { computed, Ref, ref, watch } from 'vue';

  interface IProps {
    anchor: HTMLElement;
  }

  const props = defineProps<IProps>();

  const menu: Ref<HTMLElement> = ref();
  const visible = ref(false);
  const top = ref();
  const left = ref();

  const style = computed(() => ({
    top: `${top.value}px`,
    right: `${left.value}px`,
  }));

  function open() {
    visible.value = true;
  }

  function close() {
    visible.value = false;
  }

  function toggle() {
    visible.value = !visible.value;
  }

  function adjustMenuPosition() {
    const menuRect = menu.value.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    if (menuRect.bottom > viewportHeight) {
      top.value = top.value - (menuRect.bottom - viewportHeight);
    }
    if (menuRect.top < 0) {
      top.value = 0;
    }
  }

  watch(visible, (isVisible) => {
    if (!isVisible) {
      return;
    }
    const anchorRect = props.anchor.getBoundingClientRect();
    top.value = anchorRect.top;
    left.value = window.innerWidth - anchorRect.right;
  });

  defineExpose({
    open,
    close,
    toggle,
  });
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;

  .menu {
    position: fixed;
    min-width: 25rem;
    background: white;
    display: flex;
    flex-direction: column;
    border-radius: 0.5rem;
    box-shadow: $big-shadow;
    z-index: 2;
  }
</style>
